import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  Truck, FileText, Package, Factory, FolderKanban,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import useWebSocket from '../../hooks/useWebSocket';

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
  'X-Company-ID': localStorage.getItem('company_id') || '',
});

/** One pulse cell. */
function PulseCell({ icon: Icon, label, value, tone, link, loading, justChanged }) {
  const tones = {
    neutral:  'bg-white dark:bg-neutral-900 text-neutral-700 dark:text-neutral-200 ring-neutral-200 dark:ring-neutral-800',
    info:     'bg-blue-50 dark:bg-blue-950/40 text-blue-800 dark:text-blue-200 ring-blue-200 dark:ring-blue-800',
    warning:  'bg-amber-50 dark:bg-amber-950/40 text-amber-800 dark:text-amber-200 ring-amber-200 dark:ring-amber-800',
    danger:   'bg-red-50 dark:bg-red-950/40 text-red-800 dark:text-red-200 ring-red-200 dark:ring-red-800',
    success:  'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-200 ring-emerald-200 dark:ring-emerald-800',
  };
  return (
    <Link
      to={link}
      className={cn(
        'relative flex items-center gap-3 rounded-lg px-4 py-3 ring-1 shadow-sm hover:shadow-md transition-all duration-200',
        tones[tone] || tones.neutral,
        justChanged && 'ring-2 ring-primary-400 animate-pulse',
      )}
    >
      <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-white/60 dark:bg-white/10 flex-shrink-0">
        <Icon size={18} aria-hidden="true" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-[11px] uppercase tracking-wide opacity-75 truncate">{label}</p>
        <p className="font-mono text-2xl font-bold leading-none mt-0.5 tabular-nums" style={{ fontVariantNumeric: 'tabular-nums' }}>
          {/* leading space keeps label and value from gluing in extracted text */}
          {' '}{loading ? '—' : (value ?? 0)}
        </p>
      </div>
    </Link>
  );
}

/**
 * SystemPulse — 5 live KPI cells covering operations across all modules.
 *
 * Live updates arrive via WebSocket (`pulse_update` frames). Polling remains
 * as a fallback for when the socket is disconnected — hence the longer
 * default `pollMs`.
 */
export default function SystemPulse({ pollMs = 60000, className = '' }) {
  const [pulse, setPulse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [changedKeys, setChangedKeys] = useState({});
  const prevPulseRef = useRef(null);

  const fetchPulse = useCallback(async () => {
    try {
      const base = process.env.REACT_APP_BACKEND_URL || '';
      const res = await axios.get(`${base}/api/dashboard/system-pulse`, { headers: getAuthHeaders() });
      setPulse(res.data || {});
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'تعذر تحميل النبض');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPulse();
    if (pollMs > 0) {
      const id = setInterval(fetchPulse, pollMs);
      return () => clearInterval(id);
    }
  }, [fetchPulse, pollMs]);

  // Highlight cells whose value changed between consecutive pulses.
  useEffect(() => {
    if (!pulse) return;
    const prev = prevPulseRef.current;
    if (prev) {
      const diffs = {};
      Object.keys(pulse).forEach((k) => {
        if (prev[k] !== pulse[k]) diffs[k] = true;
      });
      if (Object.keys(diffs).length) {
        setChangedKeys(diffs);
        const t = setTimeout(() => setChangedKeys({}), 2000);
        prevPulseRef.current = pulse;
        return () => clearTimeout(t);
      }
    }
    prevPulseRef.current = pulse;
  }, [pulse]);

  const handleWsMessage = useCallback((msg) => {
    if (msg.event === 'pulse_update') {
      fetchPulse();
    }
  }, [fetchPulse]);

  const { connected } = useWebSocket({
    filterEvents: ['pulse_update'],
    onMessage: handleWsMessage,
  });

  if (error && !pulse) {
    return (
      <div className={cn('rounded-lg border border-dashed border-red-200 dark:border-red-900 p-4 text-sm text-red-600 dark:text-red-400', className)} dir="rtl">
        {error}
      </div>
    );
  }

  const p = pulse || {};
  const cells = [
    {
      key: 'active_shipments',
      label: 'شحنات نشطة', icon: Truck,
      value: p.active_shipments,
      tone: (p.active_shipments || 0) > 0 ? 'info' : 'neutral',
      link: '/shipments',
    },
    {
      key: 'overdue_invoices',
      label: 'فواتير متأخرة', icon: FileText,
      value: p.overdue_invoices,
      tone: (p.overdue_invoices || 0) > 0 ? 'danger' : 'success',
      link: '/invoices',
    },
    {
      key: 'low_stock',
      label: 'مخزون منخفض', icon: Package,
      value: p.low_stock,
      tone: (p.low_stock || 0) > 0 ? 'warning' : 'success',
      link: '/products',
    },
    {
      key: 'active_work_orders',
      label: 'أوامر تصنيع', icon: Factory,
      value: p.active_work_orders,
      tone: (p.active_work_orders || 0) > 0 ? 'info' : 'neutral',
      link: '/manufacturing',
    },
    {
      key: 'delayed_projects',
      label: 'مشاريع متأخرة', icon: FolderKanban,
      value: p.delayed_projects,
      tone: (p.delayed_projects || 0) > 0 ? 'danger' : 'success',
      link: '/projects',
    },
  ];

  return (
    <div className={cn('space-y-2', className)} dir="rtl">
      <div className="flex justify-end">
        <span
          className={cn(
            'text-[11px] px-2 py-0.5 rounded-full flex items-center gap-1',
            connected
              ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
              : 'bg-neutral-100 text-neutral-500 dark:bg-neutral-800 dark:text-neutral-400',
          )}
          title={connected ? 'متصل عبر WebSocket' : 'غير متصل — يعمل polling احتياطي'}
        >
          <span
            className={cn(
              'w-1.5 h-1.5 rounded-full',
              connected ? 'bg-emerald-500 animate-pulse' : 'bg-neutral-400',
            )}
          />
          {connected ? 'مباشر' : 'غير متصل'}
        </span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        {cells.map((c) => (
          <PulseCell
            key={c.label}
            loading={loading}
            justChanged={!!changedKeys[c.key]}
            {...c}
          />
        ))}
      </div>
    </div>
  );
}
